/**
 * Video Studio for client teams (cowork API, client launch contract):
 *   GET  /v1/videos/:id/rights   {clips:[{mediaId, creator, creatorKey, campaignId, usageEndsAt, ok, reason, licence}], ok}
 *   POST /v1/videos/:id/render   {aiLabel, scale?}; 409 {error:'rights', clips} when a creator clip's licence is not
 *                                 cleared (or unknown) for a client; 429 cap_reached for caps
 *   POST /v1/videos/licences/confirm  {campaignId, creatorKey}  {licence}; 409 licence_waiting_beatly before Beatly
 *   POST /v1/videos/licences/withdraw {campaignId, creatorKey}  {licence}
 *   POST /v1/videos/licences/request  {campaignId, creatorKey, creatorLabel?}  {licence}: asks Beatly to confirm
 * A creator clip is cleared only when Beatly and the brand both confirmed its licence and today is within it.
 * plus what the editor needs to decide the "Made with AI" label: AI voice, AI music or the AI presenter in the video.
 */
import { API_BASE_URL, CoworkApiError, authHeaders, type VideoProject } from '@/cowork/omniad/api/coworkApi';
import { voicePartsOf, type VideoSpec, type VideoMedia } from '@/video/spec';

/**
 * A clip's licence as the rights answer gives it: who confirmed, until when (null = no end date), Beatly asked.
 * `requestedAt` (when Beatly was last asked) is read when the answer carries it; without it the box can still ask again.
 */
export interface ClipLicence { beatly: boolean; brand: boolean; until: string | null; requested: boolean; requestedAt?: string | null }
export interface ClipRights { mediaId: string; creator: string | null; creatorKey: string | null; campaignId: string | null; usageEndsAt: string | null; ok: boolean; reason: string | null; licence: ClipLicence | null }
export interface VideoRights { clips: ClipRights[]; ok: boolean }

/** The licence record (one per team, campaign and creator), as the licence calls answer it. */
export interface Licence {
  teamId: string; campaignId: string; creatorKey: string; creatorLabel: string;
  until: string | null;
  beatly: { by: string; at: string } | null;
  brand: { by: string; at: string } | null;
  requestedAt: string | null; requestedBy: string | null;
}

async function failed(res: Response): Promise<never> {
  const json = await res.json().catch(() => ({}));
  const body = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  throw new CoworkApiError(typeof body.error === 'string' ? body.error : `Request failed: ${res.status}`, res.status, body);
}

function asClips(v: unknown): ClipRights[] {
  if (!Array.isArray(v)) return [];
  return v.filter((c) => c && typeof c === 'object').map((c) => {
    const x = c as Record<string, unknown>;
    const s = (k: string) => (typeof x[k] === 'string' ? (x[k] as string) : null);
    return { mediaId: s('mediaId') || '', creator: s('creator'), creatorKey: s('creatorKey'), campaignId: s('campaignId'), usageEndsAt: s('usageEndsAt'), ok: x.ok === true, reason: s('reason'), licence: asClipLicence(x.licence) };
  });
}

function asClipLicence(v: unknown): ClipLicence | null {
  if (!v || typeof v !== 'object') return null;
  const x = v as Record<string, unknown>;
  const at = typeof x.requestedAt === 'string' ? x.requestedAt : null;
  return { beatly: x.beatly === true, brand: x.brand === true, until: typeof x.until === 'string' ? x.until : null, requested: x.requested === true || !!at, ...(at ? { requestedAt: at } : {}) };
}

function asLicence(v: unknown): Licence {
  const x = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const s = (k: string) => (typeof x[k] === 'string' ? (x[k] as string) : null);
  const side = (k: string) => { const o = x[k] as Record<string, unknown> | null | undefined; return o && typeof o === 'object' ? { by: typeof o.by === 'string' ? o.by : '', at: typeof o.at === 'string' ? o.at : '' } : null; };
  return { teamId: s('teamId') || '', campaignId: s('campaignId') || '', creatorKey: s('creatorKey') || '', creatorLabel: s('creatorLabel') || '', until: s('until'), beatly: side('beatly'), brand: side('brand'), requestedAt: s('requestedAt'), requestedBy: s('requestedBy') };
}

async function licenceCall(action: 'confirm' | 'withdraw' | 'request', body: Record<string, string>): Promise<{ licence: Licence }> {
  const res = await fetch(`${API_BASE_URL}/v1/videos/licences/${action}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
  if (!res.ok) return failed(res);
  const json = (await res.json()) as { licence?: unknown };
  return { licence: asLicence(json.licence) };
}

/** The brand confirms it holds the creator's licence (409 licence_waiting_beatly until Beatly confirmed). */
export const confirmLicence = (campaignId: string, creatorKey: string) => licenceCall('confirm', { campaignId, creatorKey });
/** The brand takes its confirmation back. */
export const withdrawLicence = (campaignId: string, creatorKey: string) => licenceCall('withdraw', { campaignId, creatorKey });
/** Asks Beatly to confirm the licence (the API sends it once a day per creator at most). */
export const requestLicence = (campaignId: string, creatorKey: string, creatorLabel?: string) =>
  licenceCall('request', creatorLabel ? { campaignId, creatorKey, creatorLabel } : { campaignId, creatorKey });

export async function videoRights(id: string): Promise<VideoRights> {
  const res = await fetch(`${API_BASE_URL}/v1/videos/${encodeURIComponent(id)}/rights`, { headers: authHeaders(false) });
  if (!res.ok) return failed(res);
  const json = (await res.json()) as { clips?: unknown; ok?: unknown };
  const clips = asClips(json.clips);
  return { clips, ok: json.ok === true || (json.ok === undefined && clips.every((c) => c.ok)) };
}

/**
 * `aiLabel` is sent only when the person changed the toggle: left out, the API applies its own rule (on whenever its
 * reading of the plan finds AI voice, AI music or the AI presenter; always on for a client team).
 */
export async function renderVideo(id: string, opts: { aiLabel?: boolean; scale?: number }): Promise<{ video: VideoProject }> {
  const res = await fetch(`${API_BASE_URL}/v1/videos/${encodeURIComponent(id)}/render`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(opts) });
  if (!res.ok) return failed(res);
  return res.json() as Promise<{ video: VideoProject }>;
}

/** The clips a render was refused for (409 rights), or null for any other error. */
export function rightsRefusalOf(err: unknown): ClipRights[] | null {
  if (!(err instanceof CoworkApiError) || err.status !== 409 || err.body.error !== 'rights') return null;
  return asClips(err.body.clips).filter((c) => !c.ok);
}

/** A day the way the dialogs say it: 1 Oct 2026 (the input as given when it is not a date). */
export function dayText(iso: string): string {
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "@handle" for a clip's creator (a name stays a name), or null when the clip has none. */
export function creatorLabelOf(c: Pick<ClipRights, 'creator' | 'creatorKey'>): string | null {
  const who = (c.creator || c.creatorKey || '').trim();
  if (!who) return null;
  return /\s/.test(who) ? who : `@${who.replace(/^@/, '')}`;
}

/** One line on why a clip cannot run: the API's reason, else what its licence is waiting for. */
export function clipRightsText(c: ClipRights): string {
  if (c.reason) return c.reason;
  const who = creatorLabelOf(c) || 'this creator';
  const l = c.licence;
  if (!l || !l.beatly) return `Waiting for Beatly to confirm the licence for ${who}.`;
  if (!l.brand) return `Confirm the licence for ${who} to use it in ads.`;
  if (l.until) return `The licence for ${who} ended on ${dayText(l.until)}.`;
  return `The licence for ${who} could not be checked.`;
}

const AI_MUSIC = new Set(['studio', 'library', 'generated']);
/** Audio Studio hosts: music served from there is model-made whatever the editor called its source. */
const AUDIO_STUDIO_HOSTS = ['adstudios.beatly.com'];
const hostOf = (u?: string | null) => { try { return u ? new URL(u).host.toLowerCase() : null; } catch { return null; } };
const isPresenter = (m?: VideoMedia | null) => !!m && !!m.url && (
  /\/hook-studio\/[^/]*-(recast|avatar)-[^/]*\.mp4/i.test(m.url) || /^(presenter|avatar|hook-example)[:_-]/i.test(m.sourceId || '') || /higgsfield/i.test(m.url));

/**
 * What in this video is made by AI: a voice (an AI voice-over), music (Audio Studio or generated) or a presenter.
 * Mirrors the API's reading (engine/video/aiLabel.ts aiUsage), which is what decides the label on the export: this is
 * only what the dialog says beforehand.
 */
export function aiPartsOf(spec: VideoSpec | null | undefined): Array<'voice' | 'music' | 'presenter'> {
  if (!spec) return [];
  const out: Array<'voice' | 'music' | 'presenter'> = [];
  const audio = spec.audio;
  if ((audio?.voiceoverUrl && (audio.voiceoverVoice || audio.voiceoverScript)) || audio?.voiceoverVoice || voicePartsOf(audio).some((p) => !!p.voice)) out.push('voice');
  const host = hostOf(audio?.musicUrl);
  if (audio?.musicUrl && ((audio.musicSource && AI_MUSIC.has(audio.musicSource)) || (host && AUDIO_STUDIO_HOSTS.includes(host)))) out.push('music');
  if (spec.scenes.some((sc) => { const x = sc as { media?: VideoMedia | null; others?: VideoMedia[] }; return [x.media, ...(x.others || [])].some(isPresenter); })) out.push('presenter');
  return out;
}

export function aiPartsText(parts: Array<'voice' | 'music' | 'presenter'>): string {
  const words = parts.map((p) => (p === 'voice' ? 'an AI voice' : p === 'music' ? 'AI music' : 'an AI presenter'));
  if (words.length <= 1) return words[0] || '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}
